// src/components/common/form/index.jsx
//
// Shared layout for create/edit forms, so every form reads the same way:
//
//   <FormPage title="Ny vare" subtitle="…" onBack={…}>
//     <FormSection title="Grundlæggende">
//       <TextField … />                  ← one field per line by default
//       <FormRow>                        ← short, related fields side by side
//         <TextField label="Varenr." /> <TextField label="Enhed" />
//       </FormRow>
//     </FormSection>
//     <FormActions onCancel={…} submitLabel="Opret" loading={…} />
//   </FormPage>
//
// Flexbox only; rows wrap to one field per line on narrow screens.
// Note: theme.spacing(1) is 0.25rem here (not MUI's 8px), hence the larger numbers.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, FormControlLabel, Link, Paper, Stack, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageHeader from '../PageHeader';

// Readable line length for a single column of fields
export const FORM_MAX_WIDTH = 760;

// ---------------------------------------------------------------------------
// Validation feedback
//
// Forms mark invalid fields the MUI way (`error` + `helperText`). After a
// submit attempt we read those marks back from the DOM, so every form gets the
// same behaviour without extra wiring: jump to the first invalid field and
// list all problems at the top. Errors that have no field on screen can be
// passed as `fieldErrors` so they're listed too.
// ---------------------------------------------------------------------------

const cleanLabel = (text) => (text || '').replace(/\s*\*\s*$/, '').replace(/\u200b/g, '').trim();

/** Invalid fields inside `root`: [{ label, message, focusEl }] in page order. */
export function findInvalidFields(root) {
  if (!root) return [];
  const seen = new Set();
  const fields = [];
  root.querySelectorAll('.MuiFormLabel-root.Mui-error, .MuiInputBase-root.Mui-error, .MuiFormHelperText-root.Mui-error').forEach((node) => {
    const control = node.closest('.MuiFormControl-root') || node.parentElement;
    if (!control || seen.has(control)) return;
    seen.add(control);
    const label = cleanLabel(control.querySelector('.MuiFormLabel-root, legend')?.textContent);
    const message = cleanLabel(control.querySelector('.MuiFormHelperText-root.Mui-error')?.textContent);
    const focusEl = control.querySelector('input:not([type=hidden]), textarea, [role=combobox], [tabindex="0"]');
    fields.push({ label, message, focusEl: focusEl || control });
  });
  return fields;
}

/** Scroll to and focus the first invalid field; returns the list. */
export function revealInvalidFields(root) {
  const fields = findInvalidFields(root);
  const first = fields[0];
  if (first) {
    first.focusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    first.focusEl.focus?.({ preventScroll: true });
  }
  return fields;
}

// Run after React has re-rendered with the new error state
const afterRender = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

/**
 * Page shell: back button + title/subtitle, an optional error banner, and a
 * single centred-left column of sections. Renders a <form> when `onSubmit`
 * is given, so Enter submits and FormActions' button can be type="submit".
 *
 * On a submit that leaves fields invalid, it scrolls to the first one and
 * shows a summary of everything that needs fixing. `error` (e.g. a server
 * error) is scrolled into view when it appears.
 */
export function FormPage({
  title,
  subtitle,
  onBack,
  onSubmit,
  error,
  onErrorClose,
  fieldErrors,
  maxWidth = FORM_MAX_WIDTH,
  headerActions,
  crumb,
  children,
}) {
  const { t } = useTranslation(['common']);
  const formRef = useRef(null);
  const summaryRef = useRef(null);
  const errorRef = useRef(null);
  const [summary, setSummary] = useState(null); // null = not shown

  // Field errors on screen + any extra messages without a visible field
  const collect = useCallback(() => {
    const onScreen = findInvalidFields(formRef.current).map((f) => ({
      text: f.label && f.message ? `${f.label}: ${f.message}` : (f.message || f.label),
      focusEl: f.focusEl,
    }));
    const shown = new Set(onScreen.map((i) => i.text));
    const extra = Object.values(fieldErrors || {})
      .filter((msg) => msg && typeof msg === 'string' && ![...shown].some((text) => text.includes(msg)))
      .map((msg) => ({ text: msg, focusEl: null }));
    return [...onScreen, ...extra];
  }, [fieldErrors]);

  const handleSubmit = onSubmit
    ? (e) => {
        e.preventDefault();
        onSubmit(e);
        afterRender(() => {
          const items = collect();
          setSummary(items.length ? items : null);
          if (!items.length) return;
          const target = items.find((i) => i.focusEl)?.focusEl;
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            target.focus?.({ preventScroll: true });
          } else {
            summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    : undefined;

  // Keep the summary in step with the fields while the user fixes them
  const handleChange = () => {
    if (summary) afterRender(() => { const items = collect(); setSummary(items.length ? items : null); });
  };
  useEffect(() => {
    if (summary) { const items = collect(); setSummary(items.length ? items : null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldErrors]);

  // Server errors appear at the top; make sure they're seen
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  return (
    <Box sx={{ maxWidth, width: '100%' }}>
      <PageHeader onBack={onBack} title={title} subtitle={subtitle} actions={headerActions} crumb={crumb} />

      {error && (
        <Alert ref={errorRef} severity="error" sx={{ mb: 5 }} onClose={onErrorClose}>
          {error}
        </Alert>
      )}

      {summary && (
        <Alert ref={summaryRef} severity="error" sx={{ mb: 5 }} onClose={() => setSummary(null)}>
          <AlertTitle>{t('common:form_errors.title', { count: summary.length })}</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 5 }}>
            {summary.map((item) => (
              <li key={item.text}>
                {item.focusEl ? (
                  <Link
                    component="button"
                    type="button"
                    color="inherit"
                    underline="always"
                    sx={{ textAlign: 'left', verticalAlign: 'baseline' }}
                    onClick={() => {
                      item.focusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      item.focusEl.focus?.({ preventScroll: true });
                    }}
                  >
                    {item.text}
                  </Link>
                ) : item.text}
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Stack
        ref={formRef}
        spacing={5}
        component={onSubmit ? 'form' : 'div'}
        onSubmit={handleSubmit}
        onChange={handleChange}
        noValidate={onSubmit ? true : undefined}
      >
        {children}
      </Stack>
    </Box>
  );
}

/**
 * A titled card grouping related fields. Children stack vertically.
 * `number` puts a step badge before the title (1, 2, 3 …) for long forms.
 */
export function FormSection({ title, description, number, action, children, sx }) {
  // e.g. an address section toggled off: header only, no empty body spacing
  const hasBody = React.Children.toArray(children).length > 0;
  return (
    <Paper variant="outlined" sx={{ p: { xs: 5, sm: 7 }, borderRadius: 3, ...sx }}>
      {(title || description || action) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: hasBody ? 5 : 0 }}>
          {number != null && (
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.lighter',
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            >
              {number}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title && <Typography variant="h5" fontWeight={700}>{title}</Typography>}
            {description && (
              <Typography variant="body2" color="text.secondary">{description}</Typography>
            )}
          </Box>
          {action}
        </Box>
      )}
      {hasBody && <FormStack>{children}</FormStack>}
    </Paper>
  );
}

/**
 * Vertical list of fields — use inside dialogs, where FormSection's card
 * would be one box too many.
 */
export function FormStack({ children, gap = 5 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
      {children}
    </Box>
  );
}

/**
 * Short, related fields side by side (e.g. postnummer + by). Each child grows
 * equally from `minWidth`; the row wraps to one per line when space runs out.
 * Pass `widths` (flex-grow per child, e.g. [1, 3]) for unequal columns.
 */
export function FormRow({ children, minWidth = 200, widths }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {items.map((child, i) => (
        <Box key={child.key ?? i} sx={{ flex: `${widths?.[i] ?? 1} 1 ${minWidth}px`, minWidth: 0 }}>
          {child}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Bottom action bar: Cancel, then the primary action, right-aligned.
 * `submitType="submit"` pairs with FormPage's `onSubmit`.
 */
export function FormActions({
  onCancel,
  onSubmit,
  submitLabel,
  cancelLabel,
  submitIcon,
  loading = false,
  disabled = false,
  submitType = 'button',
  children,
}) {
  const { t } = useTranslation(['common']);
  const barRef = useRef(null);
  // Outside a FormPage <form> (e.g. settings panels) the page can't do it, so
  // jump to the first invalid field in this form's area ourselves
  const handleClick = (e) => {
    onSubmit?.(e);
    afterRender(() => revealInvalidFields(barRef.current?.parentElement));
  };
  return (
    <Box ref={barRef} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      {children}
      {onCancel && (
        <Button variant="outlined" onClick={onCancel} disabled={loading}>
          {cancelLabel ?? t('common:buttons.cancel')}
        </Button>
      )}
      <Button
        type={submitType}
        variant="contained"
        onClick={submitType === 'submit' ? undefined : handleClick}
        disabled={loading || disabled}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : submitIcon}
      >
        {submitLabel ?? t('common:buttons.save')}
      </Button>
    </Box>
  );
}

/**
 * On/off setting with a label and an explanatory line underneath
 * (e.g. "Aktivt lokale — Dette lokale er aktivt og kan bruges til udstyr").
 */
export function FormSwitchField({ checked, onChange, disabled, label, description }) {
  return (
    <FormControlLabel
      sx={{ alignItems: 'flex-start', ml: 0, gap: 3 }}
      control={<Switch checked={!!checked} onChange={onChange} disabled={disabled} sx={{ mt: -1.5, ml: -3 }} />}
      label={
        <Box>
          <Typography variant="body1">{label}</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">{description}</Typography>
          )}
        </Box>
      }
    />
  );
}
